import React, { Component } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'
import isNumber from 'lodash/isNumber'
import remove from 'lodash/remove'
import Hammer from 'react-hammerjs'
import createChart from './base'
import {
  combineDataConfig,
  combineAnimateConfig,
  combineChartConfig,
  combineContentConfig,
  parseTranslateX,
  mergeDataLegend,
  parseF2Color,
} from './utils'

import styles from './Chart.module.less'
import isIphone from '@helpers/isIphone'

const screenDirection = (window.matchMedia && window.matchMedia('(orientation: portrait)')) || {}

let reboundTimer = null

const isDisabledResizeChart = (chartcode) => {
  const disableIosResizeList = ['gauge_mobile']
  return disableIosResizeList.includes(chartcode)
}

class Chart extends Component {
  static defaultProps = {
    gridPadding: 10,
    chartPadding: {
      containerPadding: [10, 10],
      chartPadding: [15, 15, 15, 15]
    },
    legendIndicator: 'type'
  }

  static propTypes = {
    chartRef: PropTypes.func,
    width: PropTypes.string || PropTypes.number,
    height: PropTypes.string || PropTypes.number,
    padding:
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])) || PropTypes.string,
    appendPadding:
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])) || PropTypes.string,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    defs: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    pixelRatio: PropTypes.number,
    isMobile: PropTypes.bool,
    gridPadding: PropTypes.number,
    chartPadding: PropTypes.shape({
      containerPadding: PropTypes.arrayOf(PropTypes.number),
      chartPadding: PropTypes.arrayOf(PropTypes.number)
    }),
    getReportMetadata: PropTypes.func,
    detailMode: PropTypes.bool,
    plugins: PropTypes.array, // 注册插件
    legendIndicator: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isCustomLegends: PropTypes.bool,
    tooltipData: PropTypes.any,
    tooltipDataProcessor: PropTypes.func,
    customTooltipRender: PropTypes.func,
    onBeforeLegendChange: PropTypes.func, // 在 legend 变化时调用
    isNotice: PropTypes.bool,
    tooltipShowTotal: PropTypes.bool // 显示总计
  }

  el = React.createRef()
  tooltipEl = React.createRef()
  /**
   *
   * @type {F2Chart}
   */
  chart = null
  config = {}
  tooltipDeltaX = 0

  // 标识是否是 state 更新时不需要重绘 f2 chart
  ignoreF2Update = false

  state = {
    tooltip: null,
    chartLegends: null,
    // 横屏状态 https://stackoverflow.com/questions/4917664/detect-viewport-orientation-if-orientation-is-portrait-display-alert-message-ad
    landscape: window.matchMedia && window.matchMedia('(orientation: landscape)').matches
  }

  transformX = 0

  componentDidMount() {
    this.createChartInstance(this.config)
    this.clearConfigData()

    // 适配移动端iphone的横竖屏切换
    // if (!this.props.detailMode && this.props.isMobile && isIphone() && !isDisabledResizeChart(this.props.chartcode)) {
    //   screenDirection.addListener(this.resizeChart)
    //   this.resizeChart(150)
    // }
    window.addEventListener('resize', this.updateOrientation)
  }

  componentDidUpdate() {
    // tooltip 修改 不需要重新更新图
    this.updateTooltipEl()

    this.repaintChartInstance(this.config)
    this.clearConfigData()
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    this.el = null
    window.removeEventListener('resize', this.updateOrientation)
    // 适配移动端iphone的横竖屏切换
    // if (!this.props.detailMode && this.props.isMobile && isIphone() && !isDisabledResizeChart(this.props.chartcode)) {
    //   screenDirection.removeListener(this.resizeChart)
    // }
  }

  render() {
    const { detailMode, isNotice } = this.props
    const { landscape } = this.state

    return (
      <div className={styles.fc2ChartComponent}>
        {detailMode && !isNotice && (
          <div className={`tooltip-content ${landscape ? 'landscape' : ''}`}>{this.renderCustomTooltip()}</div>
        )}
        <div className="canvas-content">
          <canvas style={{ width: '100%', height: '100%' }} ref={this.el}>
            {Array.isArray(this.props.children)
              ? this.props.children.map((child) => {
                if (!child) return null
                if (Array.isArray(child)) {
                  return child.map((d) => {
                    if (!d) return null
                    const { props: subProps } = d
                    return React.cloneElement(d, {
                      centralizedUpdates: this.centralizedUpdates,
                      ...subProps
                    })
                  })
                }
                const { props } = child
                return React.cloneElement(child, {
                  centralizedUpdates: this.centralizedUpdates,
                  ...props
                })
              })
              : this.props.children
                ? React.cloneElement(this.props.children, {
                  ...this.props.children.props,
                  centralizedUpdates: this.centralizedUpdates
                })
                : null}
          </canvas>
        </div>
      </div>
    )
  }

  renderCustomTooltip() {
    const { detailMode, tooltipData, customTooltipRender, tooltipDataProcessor, tooltipShowTotal } = this.props
    const { chartLegends, tooltip, landscape } = this.state

    if (!detailMode) return null

    const legends = this._getLegends()

    // 首先使用外部数据
    let items = tooltipData
    if (!items) {
      // merge tooltip 数据
      items = mergeDataLegend(tooltip && tooltip.items, legends)
    }
    // 处理数据
    if (isFunction(tooltipDataProcessor)) {
      items = tooltipDataProcessor(items, legends, chartLegends, this.chart && this.chart.chartInstance)
    }

    if (isFunction(customTooltipRender)) {
      return customTooltipRender(items, legends, chartLegends)
    }

    if (!items) return null

    if (!Array.isArray(items)) {
      items = [items]
    }

    const effectTooltipData = items.find(d => d.title)
    const title = effectTooltipData && effectTooltipData.title
    const totalData = items.filter(d => d.checked)

    if (tooltipShowTotal && totalData.length > 1) {
      let total = null
      totalData.forEach((item) => {
        const value = Number(item.origin.originValue)
        if (!Number.isNaN(value) && item.origin.originValue !== null) {
          total += value
        }
      })
      items.unshift({
        enableCheck: false,
        checked: true,
        name: '总计',
        color: 'rgba(33, 33, 33, 0.8)',
        title: totalData[0].title,
        value: total,
        origin: {
          ...totalData[0].origin,
          legendDisabled: true,
          showPercentage: false,
          legendFormatFn: totalData[0].origin.originFormatFn || totalData[0].origin.legendFormatFn,
          value: total,
          originValue: total,
          type: '总计'
        }
      })
    }

    return (
      <div
        className={`${styles.fc2ChartComponentTooltip} ${styles.detailMode}`}
        ref={this.tooltipEl}>
        {(!landscape || (landscape && title)) && (
          <div
            className="dim-title"
          >
            {title}
          </div>
        )}
        <Hammer
          onPan={this.handleTooltipSwipe}
          onTap={this.handleChartLegendSelect}
        >
          <div
            className={'item-list-wrapper'}
            style={{ height: landscape ? 28 : 50 }}
          >
            <div className="item-list">
              {items.map((d) => {
                if (!d) return null
                const unselected = !d.checked

                // 区分两种不同的格式化方式，用在 legend 中有部分需要额外的格式化
                let { legendFormatFn } = d.origin || {}
                const { formatFn } = d.origin || {}
                if (!legendFormatFn) {
                  legendFormatFn = formatFn || d.formatFn
                }
                const parsedColor = parseF2Color(d.color)
                return (
                  <div
                    key={d.name}
                    data-item={JSON.stringify(d)}
                    className={`item${unselected ? ' unselected' : ''}`}
                  >
                    {!landscape && <div className="name">{d.name === 'null' ? '(空)' : d.name}</div>}
                    <div className="detail">
                      <span
                        className="indicator"
                        style={{
                          background: parsedColor.background
                        }}
                      />
                      {landscape && <span className="name">{d.name === 'null' ? '(空)' : d.name}</span>}
                      <span
                        style={{ color: parsedColor.textColor }}
                        className="value">
                        {legendFormatFn ? legendFormatFn(d.origin, d) : d.value}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="cover cover-left" />
            <div className="cover cover-right" />
          </div>
        </Hammer>
      </div>
    )
  }

  createChartInstance(config) {
    const { chartRef, isCustomLegends } = this.props

    if (this.chart) {
      this.chart.destroy()
    }

    combineChartConfig(this.props, config)
    combineDataConfig(this.props, config)
    combineAnimateConfig(this.props, config)

    config.chart.el = this.el.current
    if (config.tooltip) {
      // 自定义 tooltip
      config.tooltip.customTooltipFn = this.updateTooltip
      config.tooltip.getCurrentTooltip = this.getCurrentTooltip
      // 区分交互方式
      config.tooltip.detailMode = this.props.detailMode
    }
    this.chart = createChart(config, isCustomLegends ? null : this.setChartLegends, this.resetTooltipEl)
    chartRef(this.chart)
  }

  repaintChartInstance(config) {
    if (this.ignoreF2Update) {
      this.ignoreF2Update = false
      return
    }
    const { chartRef, isCustomLegends } = this.props

    combineChartConfig(this.props, config)
    combineDataConfig(this.props, config)
    combineAnimateConfig(this.props, config)

    config.chart.el = this.el.current

    if (config.tooltip) {
      // 自定义 tooltip
      config.tooltip.customTooltipFn = this.updateTooltip
      config.tooltip.getCurrentTooltip = this.getCurrentTooltip
      // 区分交互方式
      config.tooltip.detailMode = this.props.detailMode
    }

    if (this.chart) {
      this.chart.repaint(config, this.state.tooltip)
    } else {
      this.chart = createChart(config, isCustomLegends ? null : this.setChartLegends, this.resetTooltipEl)
      chartRef(this.chart)
    }
  }

  clearConfigData() {
    this.config = {}
  }

  centralizedUpdates = (unit) => {
    const { config } = this
    const { props, type } = unit
    const resProps = { ...props }
    Reflect.deleteProperty(resProps, 'centralizedUpdates')

    combineContentConfig(type, resProps, config)
  }

  updateTooltip = (tooltip, cb) => {
    if (!tooltip) {
      return
    }
    // todo
    if (this.state.tooltip && isEqual(tooltip.items, this.state.tooltip.items)) {
      if (typeof cb === 'function') {
        cb(this)
      }
      return
    }

    this.ignoreF2Update = true

    this.setState({
      tooltip,
    }, () => {
      if (typeof cb === 'function') {
        cb(this)
      }
    })
  }

  updateTooltipEl(delta = null, isFinal = false) {
    const { detailMode } = this.props
    if (!detailMode) return

    if (!this.tooltipEl || !this.tooltipEl.current) return

    const coverRight = this.tooltipEl.current.querySelector('.cover-right')
    const tooltipWrapper = this.tooltipEl.current.querySelector('.item-list-wrapper')
    const list = this.tooltipEl.current.querySelector('.item-list')

    if (!coverRight || !tooltipWrapper || !list) return
    const containerWidth = tooltipWrapper.clientWidth
    const listWidth = list.clientWidth

    if (delta === null) {
      if (listWidth <= containerWidth) {
        this.transformX = 0
        coverRight.style.opacity = 0
        this.showOrHideScrollCover(false, false)
        this.showOrHideScrollCover(true, false)
      } else {
        if (containerWidth - this.transformX > listWidth) {
          this.transformX = containerWidth - listWidth
        }
        this.showOrHideScrollCover(false, Math.abs(this.transformX) < listWidth - containerWidth)
        this.showOrHideScrollCover(true, Math.abs(this.transformX) > 0)
      }
      list.style.transitionDuration = '0s'
      list.style.transform = `translateX(${this.transformX}px)`
      setTimeout(() => {
        list.style.transitionDuration = '.2s'
      }, 0)
      return
    }

    if (listWidth <= containerWidth) return
    // 当前 translateX
    const translateX = parseTranslateX(list.style.transform)
    // 宽度差异
    const distance = Math.min(Math.max(delta + translateX, containerWidth - listWidth - 20), 20)
    clearTimeout(reboundTimer)

    list.style.transform = `translateX(${distance}px)`
    this.transformX = distance
    if (distance >= 0) {
      this.showOrHideScrollCover(true, false)

      isFinal && (reboundTimer = setTimeout(() => {
        list.style.transform = 'translate(0)'
        this.transformX = 0
      }, 100))
    } else if (distance <= containerWidth - listWidth) {
      this.showOrHideScrollCover(false, false)

      isFinal &&
        (reboundTimer = setTimeout(() => {
          list.style.transform = `translateX(${containerWidth - listWidth}px)`
          this.transformX = containerWidth - listWidth
        }, 100))
    } else {
      this.showOrHideScrollCover(true, true)
      this.showOrHideScrollCover(false, true)
    }
  }

  // 显示或者隐藏tooltip滚动的左右渐变cover
  showOrHideScrollCover = (left, show) => {
    this.tooltipEl.current.querySelector(left ? '.cover-left' : '.cover-right').style.opacity = show ? 1 : 0
  }

  // 指定index滚动到视图，比如pie中点击pie之后
  scrollTooltipIntoView(index) {
    if (!this.tooltipEl || !this.tooltipEl.current) return
    const listDom = this.tooltipEl.current.querySelector('.item-list')
    const targetDom = listDom.children[index]
    if (targetDom) {
      const { offsetLeft } = targetDom
      this.transformX = -offsetLeft
      listDom.style.transform = `translateX(${this.transformX}px)`
      this.showOrHideScrollCover(true, false)
    }
  }

  handleTooltipSwipe = (e) => {
    const { deltaX, isFinal, isFirst } = e

    const prevDeltaX = this.tooltipDeltaX

    if (isFinal || isFirst) {
      this.tooltipDeltaX = 0
    } else {
      this.tooltipDeltaX = deltaX
    }
    this.updateTooltipEl(deltaX - prevDeltaX, isFinal)
  }

  handleChartLegendSelect = (e) => {
    const target = this.getTooltipLegendItem(e.target)
    if (!target) return
    let item = target.getAttribute('data-item')
    if (!item) return
    try {
      item = JSON.parse(item)
    } catch (error) {
      return
    }
    const { cancelEvent, name, checked, legendDisabled } = item
    if (cancelEvent || legendDisabled) return

    const { chartInstance } = this.chart || {}

    if (!chartInstance) return

    const { legendIndicator } = this.props

    const legends = this._getLegends()
    const { onBeforeLegendChange } = this.props

    const filteredVals = legends.filter(d => !d.checked).map(d => d.name || d.dataValue)
    if (checked) {
      filteredVals.push(name)
    } else {
      remove(filteredVals, d => d === name)
    }

    (Array.isArray(legendIndicator) ? legendIndicator : [legendIndicator])
      .forEach((indicator) => {
        chartInstance.filter(indicator, d => !filteredVals.includes(d))
      })
    if (onBeforeLegendChange) {
      onBeforeLegendChange(chartInstance, filteredVals)
    } else {
      chartInstance.repaint()
    }
    // this.state.tooltip && chartInstance.showTooltip(this.state.tooltip)
  }

  /**
   * 单图根据筛选条件重绘
   */
  repaint() {
    const { chartInstance } = this.chart || {}
    if (!chartInstance) return

    const { legendIndicator } = this.props
    const legends = this._getLegends()

    const filedVars = legends.filter(d => d.checked).map(d => d.dataValue);

    (Array.isArray(legendIndicator) ? legendIndicator : [legendIndicator])
      .forEach((indicator) => {
        chartInstance.filter(indicator, d => filedVars.includes(d))
      })
    chartInstance.repaint()
  }

  getCurrentTooltip = () => this.state.tooltip

  getTooltipLegendItem(target) {
    if (!target) return null
    let dataTarget = target
    while (
      dataTarget.nodeName.toLowerCase() !== 'div' ||
      !dataTarget.classList.contains('item')
    ) {
      if (dataTarget === this.tooltipEl.current) {
        return null
      }
      dataTarget = dataTarget.parentNode
    }

    return dataTarget.nodeName.toLowerCase() === 'div' &&
      dataTarget.classList.contains('item')
      ? dataTarget
      : null
  }

  _getLegends = () => {
    const { legendIndicator } = this.props
    const { chartLegends } = this.state
    if (!isObject(chartLegends)) return []

    let legends = []

    if (legendIndicator) {
      const indicators = Array.isArray(legendIndicator) ? legendIndicator : [legendIndicator]
      legends = Object.entries(chartLegends)
        .filter(([indicator]) => indicators.includes(indicator))
        .map(d => d[1])
        .reduce((acc, item) => [...acc, ...item], [])
    } else {
      legends = Object.values(chartLegends)[0]
    }
    return Array.isArray(legends) ? legends : []
  }

  setChartLegends = (legends) => {
    if (isEqual(legends, this.state.chartLegends)) {
      return
    }
    // 标识更新 legend, 避免 f2 单图重绘
    this.ignoreF2Update = true

    this.setState({
      chartLegends: legends,
    })
  }

  resetTooltipEl = () => {
    const { detailMode } = this.props
    if (!detailMode) return

    if (!this.tooltipEl || !this.tooltipEl.current) return
    this.transformX = 0

    const coverRight = this.tooltipEl.current.querySelector('.cover-right')
    const coverLeft = this.tooltipEl.current.querySelector('.cover-left')
    const tooltipWrapper = this.tooltipEl.current.querySelector('.item-list-wrapper')
    const list = this.tooltipEl.current.querySelector('.item-list')

    if (!coverRight || !tooltipWrapper || !list) return
    list.style.transform = ''
    coverLeft.style.opacity = 0
    const containerWidth = tooltipWrapper.clientWidth
    const listWidth = list.clientWidth
    coverRight.style.opacity = listWidth > containerWidth ? 1 : 0
  }

  updateOrientation = () => {
    this.ignoreF2Update = true
    this.setState({
      landscape: window.matchMedia && window.matchMedia('(orientation: landscape)').matches
    })
  }

  resizeChart = (timeout) => {
    const defaultTime = isIphone() ? 20 : 10
    timeout = isNumber(timeout) ? timeout : defaultTime
    // console.log('resizeChart', timeout)
    if (this._changeSizeTimer) {
      clearTimeout(this._changeSizeTimer)
      this._changeSizeTimer = null
    }
    this._changeSizeTimer = setTimeout(() => {
      // console.log('resizeChart！！', document.body.offsetWidth)
      this.chart && this.chart.chartInstance && this.chart.chartInstance.changeSize(document.body.offsetWidth)
    }, timeout)
  }
}

export default Chart
